import { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { useAdaptivePolling } from './useAdaptivePolling';
import {
  MetricDataPoint,
  PrometheusInstantVectorResult,
  PrometheusRangeVectorResult,
  queryPrometheus,
  queryPrometheusInstantBatch,
  queryPrometheusInstantVector,
  queryPrometheusRangeVector
} from '../services/grafana';
import {
  buildBotsByProviderQuery,
  buildBotsIpRegionQuery,
  zeroFillStaleProviders,
} from '../utils/telemetryFreshness';

interface UseGrafanaMetricsOptions {
  tenantId?: string | null;
  scenarioId?: string | null;
  // Optional set of scenario ids to match together as scenario_id=~"a|b". When provided and
  // non-empty this takes precedence over scenarioId — used to keep bots visible when they are
  // split across several active same-name scenarios. Falls back to [scenarioId] when omitted.
  scenarioIds?: Array<string | null | undefined>;
  refreshInterval?: number;
  timeRange?: number;
  disableLiveBuffer?: boolean;
  // Optional explicit range override (seconds since epoch)
  range?: { startSec: number; endSec: number } | null;
  enabled?: boolean;
  filters?: {
    provider?: string | null;
    region?: string | null;
    publicIp?: string | null;
    publicIpv6?: string | null;
  };
}

export interface BotsIpRegionRow {
  provider: string;
  region: string;
  public_ip: string;
  public_ipv6?: string;
}

export interface MetricsData {
  // Grafana panels
  bandwidthDown: MetricDataPoint[]; // bits/sec (positive)
  bandwidthUp: MetricDataPoint[]; // bits/sec (negative)
  packetsUp: MetricDataPoint[]; // packets/sec (positive)
  packetsDown: MetricDataPoint[]; // packets/sec (negative)
  tcpConnections: MetricDataPoint[];
  tcpConnectionsClosed: MetricDataPoint[];
  rpsTotal: MetricDataPoint[];
  rpsOk: MetricDataPoint[];
  rpsError: MetricDataPoint[];
  attackerErrors: MetricDataPoint[];

  // Derived
  successRate: MetricDataPoint[]; // percent (0-100)
  responseTime: MetricDataPoint[]; // seconds

  // Dashboard extras
  botsByProvider: Record<string, MetricDataPoint[]>;
  botsIpRegion: BotsIpRegionRow[];
}

interface MetricsCacheEntry {
  data: MetricsData;
  updatedAt: number;
  seeded: boolean;
}

const METRICS_CACHE_TTL_MS = 2 * 60 * 1000;
const metricsCache = new Map<string, MetricsCacheEntry>();
const MAX_SEED_POINTS = 240;
const LIVE_BUFFER_SECONDS = 30 * 60;

// Average request duration. Bots ship metrics through the decoupled sender
// (bot/attack_folder/metrics_sender.py), which re-emits the duration histogram as two
// cumulative counters ("<name>.sum" / "<name>.count"); the collector's
// UnderscoreEscapingWithSuffixes translation names those
// http_client_request_duration_sum_seconds_total and http_client_request_duration_count_total.
// The `or` branch keeps the legacy in-process histogram series (…_seconds_sum/_count) working.
// Both sides are aggregated so the panel shows the request-weighted fleet average rather than
// whichever single bot/status-code series Prometheus happened to return first.
const buildResponseTimeQuery = (selector: string) =>
  `(sum(rate(http_client_request_duration_sum_seconds_total${selector}[1m])) / sum(rate(http_client_request_duration_count_total${selector}[1m])))` +
  ` or (sum(rate(http_client_request_duration_seconds_sum${selector}[1m])) / sum(rate(http_client_request_duration_seconds_count${selector}[1m])))`;

export const useGrafanaMetrics = (options: UseGrafanaMetricsOptions) => {
  const {
    tenantId,
    scenarioId,
    scenarioIds,
    refreshInterval = 5000,
    timeRange = 300,
    disableLiveBuffer = false,
    range,
    filters,
    enabled,
  } = options;
  const isEnabled = enabled !== false;
  // Normalize the scenario scope to a stable, order-independent key. Prefer the explicit id
  // set (dashboards pass every active same-name scenario id so bots split across them stay
  // visible); fall back to the single scenarioId. Empty string => no scenario selected.
  const scenarioKey = useMemo(() => {
    const raw = (scenarioIds && scenarioIds.length ? scenarioIds : [scenarioId])
      .map((value) => (value || '').trim())
      .filter(Boolean);
    return Array.from(new Set(raw)).sort().join('|');
  }, [scenarioIds, scenarioId]);
  const rangeStartSec =
    range && Number.isFinite(range.startSec) ? Math.floor(range.startSec) : null;
  const rangeEndSec =
    range && Number.isFinite(range.endSec) ? Math.floor(range.endSec) : null;
  const filtersKey = `${filters?.provider || ''}|${filters?.region || ''}|${filters?.publicIp || ''}|${filters?.publicIpv6 || ''}`;
  const rangeKey =
    rangeStartSec !== null && rangeEndSec !== null
      ? `${rangeStartSec}-${rangeEndSec}`
      : '';
  const isLiveRange = !rangeKey;
  const bufferSeconds = isLiveRange
    ? (disableLiveBuffer ? timeRange : Math.max(timeRange, LIVE_BUFFER_SECONDS))
    : timeRange;
  const maxPoints = Math.max(2, Math.ceil((bufferSeconds * 1000) / refreshInterval) + 1);
  const adaptiveIntervalMs = useAdaptivePolling({
    activeMs: refreshInterval,
    idleMs: Math.max(30_000, refreshInterval),
  });

  const cacheKey = `${tenantId || ''}|${scenarioKey}|${bufferSeconds}|${rangeKey}|${filtersKey}`;
  const baseKey = `${tenantId || ''}|${scenarioKey}|${rangeKey}|${filtersKey}`;
  const cacheEntry = metricsCache.get(cacheKey);
  const cacheFresh =
    !!cacheEntry &&
    Date.now() - cacheEntry.updatedAt < METRICS_CACHE_TTL_MS &&
    !rangeKey &&
    !!tenantId &&
    !!scenarioKey;

  const createEmptyMetrics = useCallback((): MetricsData => ({
    bandwidthDown: [],
    bandwidthUp: [],
    packetsUp: [],
    packetsDown: [],
    tcpConnections: [],
    tcpConnectionsClosed: [],
    rpsTotal: [],
    rpsOk: [],
    rpsError: [],
    attackerErrors: [],
    successRate: [],
    responseTime: [],
    botsByProvider: {},
    botsIpRegion: [],
  }), []);

  const [metrics, setMetrics] = useState<MetricsData>(() => {
    if (cacheFresh) {
      return cacheEntry!.data;
    }
    return createEmptyMetrics();
  });
  
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const hasSeededRef = useRef(cacheFresh ? cacheEntry!.seeded : false);
  const prevBaseKeyRef = useRef(baseKey);
  const prevBufferSecondsRef = useRef(bufferSeconds);

  const escapeLabelValue = useCallback((value: string) => {
    return value.replace(/\\/g, '\\\\').replace(/"/g, '\\"');
  }, []);

  const buildLabelSelector = useCallback(() => {
    const parts: string[] = [];
    parts.push(`tenant_id="${escapeLabelValue(tenantId || '')}"`);
    // scenarioKey may hold several ids (bots split across same-name scenarios). Match them all
    // with an anchored regex; a single id keeps the cheaper exact match. Scenario ids are UUIDs,
    // so strip anything outside [A-Za-z0-9_-] to keep the =~ value regex-safe (Prometheus =~ is
    // fully anchored, so the alternation matches each id exactly).
    const scenarioIdList = scenarioKey ? scenarioKey.split('|') : [];
    if (scenarioIdList.length > 1) {
      const alternation = scenarioIdList
        .map((id) => id.replace(/[^a-zA-Z0-9_-]/g, ''))
        .filter(Boolean)
        .join('|');
      parts.push(`scenario_id=~"${alternation}"`);
    } else {
      parts.push(`scenario_id="${escapeLabelValue(scenarioIdList[0] || '')}"`);
    }
    if (filters?.provider) parts.push(`provider="${escapeLabelValue(filters.provider)}"`);
    if (filters?.region) parts.push(`region="${escapeLabelValue(filters.region)}"`);
    if (filters?.publicIp) parts.push(`public_ip="${escapeLabelValue(filters.publicIp)}"`);
    if (filters?.publicIpv6) parts.push(`public_ipv6="${escapeLabelValue(filters.publicIpv6)}"`);
    return `{${parts.join(',')}}`;
  }, [
    escapeLabelValue,
    filters?.provider,
    filters?.publicIp,
    filters?.publicIpv6,
    filters?.region,
    scenarioKey,
    tenantId,
  ]);

  const mergeMetricSeries = useCallback(
    (prev: MetricDataPoint[], next: MetricDataPoint[], minTimestampMs: number) => {
      if (next.length === 0) {
        return [];
      }

      const mergedMap = new Map<number, number>();
      prev.forEach((point) => mergedMap.set(point.timestamp, point.value));
      next.forEach((point) => mergedMap.set(point.timestamp, point.value));

      return Array.from(mergedMap.entries())
        .map(([timestamp, value]) => ({ timestamp, value }))
        .sort((a, b) => a.timestamp - b.timestamp)
        .filter((point) => point.timestamp >= minTimestampMs);
    },
    []
  );

  const trimMetricSeries = useCallback((series: MetricDataPoint[], minTimestampMs: number) => {
    return series.filter((point) => point.timestamp >= minTimestampMs);
  }, []);

  const mergeBotsByProviderMaps = useCallback(
    (
      prev: Record<string, MetricDataPoint[]>,
      next: Record<string, MetricDataPoint[]>,
      minTimestampMs: number
    ) => {
      const merged: Record<string, MetricDataPoint[]> = { ...prev };
      Object.entries(next).forEach(([provider, points]) => {
        merged[provider] = mergeMetricSeries(merged[provider] || [], points, minTimestampMs);
      });
      Object.keys(merged).forEach((provider) => {
        if (merged[provider].length === 0) delete merged[provider];
      });
      return merged;
    },
    [mergeMetricSeries]
  );

  const appendMetricPoint = useCallback(
    (prev: MetricDataPoint[], point: MetricDataPoint, minTimestampMs: number) => {
      const nextSeries = [...prev, point].filter((p) => p.timestamp >= minTimestampMs);
      if (nextSeries.length > 1 && nextSeries[nextSeries.length - 1].timestamp === nextSeries[nextSeries.length - 2].timestamp) {
        nextSeries[nextSeries.length - 2] = point;
        nextSeries.pop();
      }
      if (nextSeries.length > maxPoints) {
        nextSeries.splice(0, nextSeries.length - maxPoints);
      }
      return nextSeries;
    },
    [maxPoints]
  );

  const normalizeMetricValue = (value: number | null, fallback = 0) =>
    typeof value === 'number' && Number.isFinite(value) ? value : fallback;

  const computeSuccessRateSeries = useCallback((okSeries: MetricDataPoint[], totalSeries: MetricDataPoint[]) => {
    const totalByTs = new Map<number, number>();
    totalSeries.forEach((p) => totalByTs.set(p.timestamp, p.value));

    return okSeries
      .map((p) => {
        const total = totalByTs.get(p.timestamp);
        if (!total || total <= 0) {
          return null;
        }
        return { timestamp: p.timestamp, value: (p.value / total) * 100 };
      })
      .filter((p): p is MetricDataPoint => p !== null);
  }, []);

  const buildBotsIpRegionRows = useCallback((vector: PrometheusInstantVectorResult[]) => {
    const invalidIpv6 = new Set(['', 'unknown', 'undefined']);
    return vector
      .map((entry) => {
        const rawV6 = entry.metric?.public_ipv6 || entry.metric?.host_public_ipv6 || '';
        return {
          provider: entry.metric?.provider || entry.metric?.cloud_provider || 'unknown',
          region: entry.metric?.region || entry.metric?.cloud_region || 'unknown',
          public_ip: entry.metric?.public_ip || entry.metric?.host_public_ip || '',
          public_ipv6: invalidIpv6.has(rawV6.toLowerCase()) ? undefined : rawV6,
        };
      })
      .filter((row) => row.public_ip || row.public_ipv6);
  }, []);

  const normalizeProviderLabel = useCallback((raw?: string | null) => {
    const value = (raw || '').trim();
    if (!value) return 'Unknown';
    const lower = value.toLowerCase();
    if (lower === 'aws') return 'AWS';
    if (lower === 'gcp') return 'GCP';
    if (lower === 'azure') return 'Azure';
    if (lower === 'openstack') return 'OpenStack';
    return value;
  }, []);

  const resolveProviderLabel = useCallback(
    (metric?: Record<string, string>) => {
      const candidates = [
        metric?.cloud_provider,
        metric?.provider,
        metric?.provider_type,
        metric?.provider_name,
      ]
        .map((value) => (value || '').trim())
        .filter((value) => value.length > 0);
      const preferred =
        candidates.find((value) => value.toLowerCase() !== 'unknown') ??
        candidates[0] ??
        '';
      return normalizeProviderLabel(preferred);
    },
    [normalizeProviderLabel]
  );

  const buildBotsByProviderFromRange = useCallback((rangeSeries: PrometheusRangeVectorResult[]) => {
    const providerMap: Record<string, MetricDataPoint[]> = {};
    for (const series of rangeSeries) {
      const provider = resolveProviderLabel(series.metric);
      providerMap[provider] = series.values;
    }
    return providerMap;
  }, [resolveProviderLabel]);

  const fetchMetrics = useCallback(async () => {
    if (!isEnabled || !tenantId || !scenarioKey) return;

    setLoading(true);
    setError(null);

    const hasValidRangeOverride =
      rangeStartSec !== null &&
      rangeEndSec !== null &&
      rangeEndSec > rangeStartSec;

    const end = hasValidRangeOverride ? rangeEndSec : Math.floor(Date.now() / 1000);
    const start = hasValidRangeOverride ? rangeStartSec : end - bufferSeconds;
    const rangeSeconds = Math.max(1, end - start);
    const seedStepSeconds = Math.max(2, Math.ceil(rangeSeconds / MAX_SEED_POINTS));
    const selector = buildLabelSelector();
    const prevRange = prevBufferSecondsRef.current;
    // Bots-IP / Region lookback (CAO-180). Live views ask "which bots are up right
    // now", so they use the telemetry freshness window (the builder's default) and a
    // reduced fleet drops off within one poll. Only an explicit, closed range asks
    // "which bots took part in this window" — and that one is evaluated at the end of
    // the range, so its wide lookback stays inside the window the user selected
    // instead of trailing wall-clock now.
    const botsIpLookbackSeconds = hasValidRangeOverride ? rangeSeconds : undefined;
    const botsIpEvalAtSec = hasValidRangeOverride ? end : undefined;
    const shouldExpandRange =
      !hasValidRangeOverride &&
      hasSeededRef.current &&
      typeof prevRange === 'number' &&
      bufferSeconds > prevRange;

    try {
      if (shouldExpandRange) {
        const olderEnd = end - prevRange;
        const olderStart = end - bufferSeconds;
        if (olderEnd > olderStart) {
          const expandSeconds = Math.max(1, olderEnd - olderStart);
          const expandStepSeconds = Math.max(2, Math.ceil(expandSeconds / MAX_SEED_POINTS));
          const [
            bandwidthDownData,
            bandwidthUpData,
            packetsUpData,
            packetsDownData,
            tcpConnectionsData,
            tcpConnectionsClosedData,
            rpsTotalData,
            rpsOkData,
            rpsErrorData,
            attackerErrorsData,
            responseTimeData,
            botsByProviderRange,
            botsIpRegionVector,
          ] = await Promise.all([
            queryPrometheus(
              `sum(irate(network_bandwidth_down_bytes_total${selector}[20s])*8)`,
              olderStart,
              olderEnd,
              expandStepSeconds
            ),
            queryPrometheus(
              `sum(irate(network_bandwidth_up_bytes_total${selector}[20s])*8*(-1))`,
              olderStart,
              olderEnd,
              expandStepSeconds
            ),
            queryPrometheus(
              `sum(irate(network_packets_up_total${selector}[20s]))`,
              olderStart,
              olderEnd,
              expandStepSeconds
            ),
            queryPrometheus(
              `sum(irate(network_packets_down_total${selector}[20s]))*(-1)`,
              olderStart,
              olderEnd,
              expandStepSeconds
            ),
            queryPrometheus(
              `sum(rate(network_connections_total${selector}[1m]))`,
              olderStart,
              olderEnd,
              expandStepSeconds
            ),
            queryPrometheus(
              `sum(rate(network_connections_closed_total${selector}[1m]))`,
              olderStart,
              olderEnd,
              expandStepSeconds
            ),
            queryPrometheus(
              `sum(irate(http_client_request_count_total${selector}[20s]))`,
              olderStart,
              olderEnd,
              expandStepSeconds
            ),
            queryPrometheus(
              `sum(irate(http_client_request_ok_total${selector}[20s]))`,
              olderStart,
              olderEnd,
              expandStepSeconds
            ),
            queryPrometheus(
              `sum(irate(http_client_request_error_total${selector}[20s]))`,
              olderStart,
              olderEnd,
              expandStepSeconds
            ),
            queryPrometheus(
              `sum(irate(attacker_errors_total${selector}[20s]))`,
              olderStart,
              olderEnd,
              expandStepSeconds
            ),
            queryPrometheus(
              buildResponseTimeQuery(selector),
              olderStart,
              olderEnd,
              expandStepSeconds
            ),
            queryPrometheusRangeVector(
              buildBotsByProviderQuery(selector),
              olderStart,
              olderEnd,
              expandStepSeconds
            ),
            queryPrometheusInstantVector(
              buildBotsIpRegionQuery(selector, botsIpLookbackSeconds),
              botsIpEvalAtSec
            ),
          ]);

          const hasOlderData =
            bandwidthDownData.length > 0 ||
            bandwidthUpData.length > 0 ||
            packetsUpData.length > 0 ||
            packetsDownData.length > 0 ||
            tcpConnectionsData.length > 0 ||
            tcpConnectionsClosedData.length > 0 ||
            rpsTotalData.length > 0 ||
            rpsOkData.length > 0 ||
            rpsErrorData.length > 0 ||
            attackerErrorsData.length > 0 ||
            responseTimeData.length > 0;

          if (!hasOlderData) {
            const [
              fullBandwidthDownData,
              fullBandwidthUpData,
              fullPacketsUpData,
              fullPacketsDownData,
              fullTcpConnectionsData,
              fullTcpConnectionsClosedData,
              fullRpsTotalData,
              fullRpsOkData,
              fullRpsErrorData,
              fullAttackerErrorsData,
              fullResponseTimeData,
              fullBotsByProviderRange,
              fullBotsIpRegionVector,
            ] = await Promise.all([
              queryPrometheus(
                `sum(irate(network_bandwidth_down_bytes_total${selector}[20s])*8)`,
                start,
                end,
                seedStepSeconds
              ),
              queryPrometheus(
                `sum(irate(network_bandwidth_up_bytes_total${selector}[20s])*8*(-1))`,
                start,
                end,
                seedStepSeconds
              ),
              queryPrometheus(
                `sum(irate(network_packets_up_total${selector}[20s]))`,
                start,
                end,
                seedStepSeconds
              ),
              queryPrometheus(
                `sum(irate(network_packets_down_total${selector}[20s]))*(-1)`,
                start,
                end,
                seedStepSeconds
              ),
              queryPrometheus(
                `sum(rate(network_connections_total${selector}[1m]))`,
                start,
                end,
                seedStepSeconds
              ),
              queryPrometheus(
                `sum(rate(network_connections_closed_total${selector}[1m]))`,
                start,
                end,
                seedStepSeconds
              ),
              queryPrometheus(
                `sum(irate(http_client_request_count_total${selector}[20s]))`,
                start,
                end,
                seedStepSeconds
              ),
              queryPrometheus(
                `sum(irate(http_client_request_ok_total${selector}[20s]))`,
                start,
                end,
                seedStepSeconds
              ),
              queryPrometheus(
                `sum(irate(http_client_request_error_total${selector}[20s]))`,
                start,
                end,
                seedStepSeconds
              ),
              queryPrometheus(
                `sum(irate(attacker_errors_total${selector}[20s]))`,
                start,
                end,
                seedStepSeconds
              ),
              queryPrometheus(
                buildResponseTimeQuery(selector),
                start,
                end,
                seedStepSeconds
              ),
              queryPrometheusRangeVector(
                buildBotsByProviderQuery(selector),
                start,
                end,
                seedStepSeconds
              ),
              queryPrometheusInstantVector(
                buildBotsIpRegionQuery(selector, botsIpLookbackSeconds),
                botsIpEvalAtSec
              ),
            ]);

            const minTimestampMs = (end - bufferSeconds) * 1000;
            const successRateSeed = computeSuccessRateSeries(fullRpsOkData, fullRpsTotalData);

            setMetrics((prev) => ({
              bandwidthDown: mergeMetricSeries(prev.bandwidthDown, fullBandwidthDownData, minTimestampMs),
              bandwidthUp: mergeMetricSeries(prev.bandwidthUp, fullBandwidthUpData, minTimestampMs),
              packetsUp: mergeMetricSeries(prev.packetsUp, fullPacketsUpData, minTimestampMs),
              packetsDown: mergeMetricSeries(prev.packetsDown, fullPacketsDownData, minTimestampMs),
              tcpConnections: mergeMetricSeries(prev.tcpConnections, fullTcpConnectionsData, minTimestampMs),
              tcpConnectionsClosed: mergeMetricSeries(prev.tcpConnectionsClosed, fullTcpConnectionsClosedData, minTimestampMs),
              rpsTotal: mergeMetricSeries(prev.rpsTotal, fullRpsTotalData, minTimestampMs),
              rpsOk: mergeMetricSeries(prev.rpsOk, fullRpsOkData, minTimestampMs),
              rpsError: mergeMetricSeries(prev.rpsError, fullRpsErrorData, minTimestampMs),
              attackerErrors: mergeMetricSeries(prev.attackerErrors, fullAttackerErrorsData, minTimestampMs),
              successRate: mergeMetricSeries(prev.successRate, successRateSeed, minTimestampMs),
              responseTime: mergeMetricSeries(prev.responseTime, fullResponseTimeData, minTimestampMs),
              botsByProvider: mergeBotsByProviderMaps(
                prev.botsByProvider,
                zeroFillStaleProviders(
                  buildBotsByProviderFromRange(fullBotsByProviderRange),
                  end * 1000,
                  seedStepSeconds
                ),
                minTimestampMs
              ),
              botsIpRegion: buildBotsIpRegionRows(fullBotsIpRegionVector),
            }));
            return;
          }

          const minTimestampMs = (end - bufferSeconds) * 1000;
          const successRateSeed = computeSuccessRateSeries(rpsOkData, rpsTotalData);

          setMetrics((prev) => ({
            bandwidthDown: mergeMetricSeries(prev.bandwidthDown, bandwidthDownData, minTimestampMs),
            bandwidthUp: mergeMetricSeries(prev.bandwidthUp, bandwidthUpData, minTimestampMs),
            packetsUp: mergeMetricSeries(prev.packetsUp, packetsUpData, minTimestampMs),
            packetsDown: mergeMetricSeries(prev.packetsDown, packetsDownData, minTimestampMs),
            tcpConnections: mergeMetricSeries(prev.tcpConnections, tcpConnectionsData, minTimestampMs),
            tcpConnectionsClosed: mergeMetricSeries(prev.tcpConnectionsClosed, tcpConnectionsClosedData, minTimestampMs),
            rpsTotal: mergeMetricSeries(prev.rpsTotal, rpsTotalData, minTimestampMs),
            rpsOk: mergeMetricSeries(prev.rpsOk, rpsOkData, minTimestampMs),
            rpsError: mergeMetricSeries(prev.rpsError, rpsErrorData, minTimestampMs),
            attackerErrors: mergeMetricSeries(prev.attackerErrors, attackerErrorsData, minTimestampMs),
            successRate: mergeMetricSeries(prev.successRate, successRateSeed, minTimestampMs),
            responseTime: mergeMetricSeries(prev.responseTime, responseTimeData, minTimestampMs),
            botsByProvider: mergeBotsByProviderMaps(
              prev.botsByProvider,
              buildBotsByProviderFromRange(botsByProviderRange),
              minTimestampMs
            ),
            botsIpRegion: buildBotsIpRegionRows(botsIpRegionVector),
          }));
        }
        return;
      }

      if (!hasSeededRef.current) {
        const [
          bandwidthDownData,
          bandwidthUpData,
          packetsUpData,
          packetsDownData,
          tcpConnectionsData,
          tcpConnectionsClosedData,
          rpsTotalData,
          rpsOkData,
          rpsErrorData,
          attackerErrorsData,
          responseTimeData,
          botsByProviderRange,
          botsIpRegionVector,
        ] = await Promise.all([
          // Bandwidth Down (bits/sec)
          queryPrometheus(
            `sum(irate(network_bandwidth_down_bytes_total${selector}[20s])*8)`,
            start,
            end,
            seedStepSeconds
          ),
          
          // Bandwidth Up (bits/sec, inverted like Grafana)
          queryPrometheus(
            `sum(irate(network_bandwidth_up_bytes_total${selector}[20s])*8*(-1))`,
            start,
            end,
            seedStepSeconds
          ),
          
          // Packets up
          queryPrometheus(
            `sum(irate(network_packets_up_total${selector}[20s]))`,
            start,
            end,
            seedStepSeconds
          ),
          
          // Packets down (inverted like Grafana)
          queryPrometheus(
            `sum(irate(network_packets_down_total${selector}[20s]))*(-1)`,
            start,
            end,
            seedStepSeconds
          ),
          
          // TCP connections
          queryPrometheus(
            `sum(rate(network_connections_total${selector}[1m]))`,
            start,
            end,
            seedStepSeconds
          ),
          
          // TCP connections closed
          queryPrometheus(
            `sum(rate(network_connections_closed_total${selector}[1m]))`,
            start,
            end,
            seedStepSeconds
          ),

          // RPS total
          queryPrometheus(
            `sum(irate(http_client_request_count_total${selector}[20s]))`,
            start,
            end,
            seedStepSeconds
          ),

          // RPS OK
          queryPrometheus(
            `sum(irate(http_client_request_ok_total${selector}[20s]))`,
            start,
            end,
            seedStepSeconds
          ),

          // RPS Error
          queryPrometheus(
            `sum(irate(http_client_request_error_total${selector}[20s]))`,
            start,
            end,
            seedStepSeconds
          ),

          // Attacker errors per second
          queryPrometheus(
            `sum(irate(attacker_errors_total${selector}[20s]))`,
            start,
            end,
            seedStepSeconds
          ),

          // Response time (avg seconds) – best-effort (metric name depends on exporter)
          queryPrometheus(
            buildResponseTimeQuery(selector),
            start,
            end,
            seedStepSeconds
          ),

          // Bots by provider (range, multi-series)
          queryPrometheusRangeVector(
            buildBotsByProviderQuery(selector),
            start,
            end,
            seedStepSeconds
          ),

          // Bots-IP / Region (instant vector)
          queryPrometheusInstantVector(
            buildBotsIpRegionQuery(selector, botsIpLookbackSeconds),
            botsIpEvalAtSec
          ),
        ]);

        const minTimestampMs = start * 1000;
        const successRateSeed = computeSuccessRateSeries(rpsOkData, rpsTotalData);

        setMetrics((prev) => ({
          bandwidthDown: mergeMetricSeries(prev.bandwidthDown, bandwidthDownData, minTimestampMs),
          bandwidthUp: mergeMetricSeries(prev.bandwidthUp, bandwidthUpData, minTimestampMs),
          packetsUp: mergeMetricSeries(prev.packetsUp, packetsUpData, minTimestampMs),
          packetsDown: mergeMetricSeries(prev.packetsDown, packetsDownData, minTimestampMs),
          tcpConnections: mergeMetricSeries(prev.tcpConnections, tcpConnectionsData, minTimestampMs),
          tcpConnectionsClosed: mergeMetricSeries(prev.tcpConnectionsClosed, tcpConnectionsClosedData, minTimestampMs),
          rpsTotal: mergeMetricSeries(prev.rpsTotal, rpsTotalData, minTimestampMs),
          rpsOk: mergeMetricSeries(prev.rpsOk, rpsOkData, minTimestampMs),
          rpsError: mergeMetricSeries(prev.rpsError, rpsErrorData, minTimestampMs),
          attackerErrors: mergeMetricSeries(prev.attackerErrors, attackerErrorsData, minTimestampMs),
          successRate: mergeMetricSeries(prev.successRate, successRateSeed, minTimestampMs),
          responseTime: mergeMetricSeries(prev.responseTime, responseTimeData, minTimestampMs),
          botsByProvider: zeroFillStaleProviders(
            buildBotsByProviderFromRange(botsByProviderRange),
            end * 1000,
            seedStepSeconds
          ),
          botsIpRegion: buildBotsIpRegionRows(botsIpRegionVector),
        }));

        // Only switch to instant append mode for relative time ranges
        hasSeededRef.current = !hasValidRangeOverride;
      } else {
        const nowMs = Date.now();
        const minTimestampMs = nowMs - bufferSeconds * 1000;

        const queryMap = {
          bandwidthDown: `sum(irate(network_bandwidth_down_bytes_total${selector}[20s])*8)`,
          bandwidthUp: `sum(irate(network_bandwidth_up_bytes_total${selector}[20s])*8*(-1))`,
          packetsUp: `sum(irate(network_packets_up_total${selector}[20s]))`,
          packetsDown: `sum(irate(network_packets_down_total${selector}[20s]))*(-1)`,
          tcpConnections: `sum(rate(network_connections_total${selector}[1m]))`,
          tcpConnectionsClosed: `sum(rate(network_connections_closed_total${selector}[1m]))`,
          rpsTotal: `sum(irate(http_client_request_count_total${selector}[20s]))`,
          rpsOk: `sum(irate(http_client_request_ok_total${selector}[20s]))`,
          rpsError: `sum(irate(http_client_request_error_total${selector}[20s]))`,
          attackerErrors: `sum(irate(attacker_errors_total${selector}[20s]))`,
          responseTime: buildResponseTimeQuery(selector),
          botsByProviderVector:
            buildBotsByProviderQuery(selector),
          botsIpRegionVector:
            buildBotsIpRegionQuery(selector, botsIpLookbackSeconds),
        };

        const batchResults = await queryPrometheusInstantBatch(Object.values(queryMap));

        const parseInstant = (data: any): number | null => {
          const result = data?.data?.result;
          if (!result || result.length === 0) return null;
          const value = result[0]?.value?.[1];
          return value == null ? null : parseFloat(value);
        };

        const parseInstantVector = (data: any): PrometheusInstantVectorResult[] => {
          const result = data?.data?.result;
          if (!Array.isArray(result)) return [];
          return result.map((entry: any) => ({
            metric: entry.metric || {},
            value: parseFloat(entry.value?.[1] ?? '0'),
          }));
        };

        const bandwidthDown = parseInstant(batchResults[queryMap.bandwidthDown]);
        const bandwidthUp = parseInstant(batchResults[queryMap.bandwidthUp]);
        const packetsUp = parseInstant(batchResults[queryMap.packetsUp]);
        const packetsDown = parseInstant(batchResults[queryMap.packetsDown]);
        const tcpConnections = parseInstant(batchResults[queryMap.tcpConnections]);
        const tcpConnectionsClosed = parseInstant(batchResults[queryMap.tcpConnectionsClosed]);
        const rpsTotal = parseInstant(batchResults[queryMap.rpsTotal]);
        const rpsOk = parseInstant(batchResults[queryMap.rpsOk]);
        const rpsError = parseInstant(batchResults[queryMap.rpsError]);
        const attackerErrors = parseInstant(batchResults[queryMap.attackerErrors]);
        const responseTime = parseInstant(batchResults[queryMap.responseTime]);
        const botsByProviderVector = parseInstantVector(batchResults[queryMap.botsByProviderVector]);
        const botsIpRegionVector = parseInstantVector(batchResults[queryMap.botsIpRegionVector]);

        const rpsTotalValue = normalizeMetricValue(rpsTotal, 0);
        const rpsOkValue = normalizeMetricValue(rpsOk, 0);
        const successRatePoint = rpsTotalValue > 0 ? (rpsOkValue / rpsTotalValue) * 100 : 0;

        setMetrics((prev) => ({
          bandwidthDown: appendMetricPoint(
            prev.bandwidthDown,
            { timestamp: nowMs, value: normalizeMetricValue(bandwidthDown, 0) },
            minTimestampMs
          ),
          bandwidthUp: appendMetricPoint(
            prev.bandwidthUp,
            { timestamp: nowMs, value: normalizeMetricValue(bandwidthUp, 0) },
            minTimestampMs
          ),
          packetsUp: appendMetricPoint(
            prev.packetsUp,
            { timestamp: nowMs, value: normalizeMetricValue(packetsUp, 0) },
            minTimestampMs
          ),
          packetsDown: appendMetricPoint(
            prev.packetsDown,
            { timestamp: nowMs, value: normalizeMetricValue(packetsDown, 0) },
            minTimestampMs
          ),
          tcpConnections: appendMetricPoint(
            prev.tcpConnections,
            { timestamp: nowMs, value: normalizeMetricValue(tcpConnections, 0) },
            minTimestampMs
          ),
          tcpConnectionsClosed: appendMetricPoint(
            prev.tcpConnectionsClosed,
            { timestamp: nowMs, value: normalizeMetricValue(tcpConnectionsClosed, 0) },
            minTimestampMs
          ),
          rpsTotal: appendMetricPoint(
            prev.rpsTotal,
            { timestamp: nowMs, value: rpsTotalValue },
            minTimestampMs
          ),
          rpsOk: appendMetricPoint(
            prev.rpsOk,
            { timestamp: nowMs, value: rpsOkValue },
            minTimestampMs
          ),
          rpsError: appendMetricPoint(
            prev.rpsError,
            { timestamp: nowMs, value: normalizeMetricValue(rpsError, 0) },
            minTimestampMs
          ),
          attackerErrors: appendMetricPoint(
            prev.attackerErrors,
            { timestamp: nowMs, value: normalizeMetricValue(attackerErrors, 0) },
            minTimestampMs
          ),
          successRate: appendMetricPoint(
            prev.successRate,
            { timestamp: nowMs, value: successRatePoint },
            minTimestampMs
          ),
          responseTime: appendMetricPoint(
            prev.responseTime,
            { timestamp: nowMs, value: normalizeMetricValue(responseTime, 0) },
            minTimestampMs
          ),
          botsByProvider: (() => {
            // Count providers actively reporting in the last 20s — the same
            // `< bool 20` definition used to seed the range chart.
            const currentCounts = new Map<string, number>();
            (botsByProviderVector || []).forEach((entry) => {
              const provider = resolveProviderLabel(entry.metric);
              currentCounts.set(provider, (currentCounts.get(provider) || 0) + entry.value);
            });

            // Append a point for every provider we currently see AND every
            // provider we have seen before. Providers with no fresh data get an
            // explicit 0 so their line drops to zero instead of freezing at the
            // last known value when bots stop reporting.
            const nextMap: Record<string, MetricDataPoint[]> = { ...prev.botsByProvider };
            const providers = new Set<string>([
              ...Object.keys(nextMap),
              ...Array.from(currentCounts.keys()),
            ]);
            providers.forEach((provider) => {
              nextMap[provider] = appendMetricPoint(
                nextMap[provider] || [],
                { timestamp: nowMs, value: currentCounts.get(provider) ?? 0 },
                minTimestampMs
              );
            });

            if (Object.keys(nextMap).length > 1) {
              Object.keys(nextMap).forEach((key) => {
                if (key.toLowerCase() === 'unknown') {
                  delete nextMap[key];
                }
              });
            }
            return nextMap;
          })(),
          botsIpRegion: buildBotsIpRegionRows(botsIpRegionVector),
        }));
      }

    } catch (err: any) {
      console.error('Error fetching metrics:', err);
      const detail =
        err?.response?.data?.detail ||
        err?.message ||
        'Failed to fetch metrics from Grafana';
      setError(detail);
    } finally {
      setLoading(false);
    }
  }, [
    appendMetricPoint,
    bufferSeconds,
    buildBotsByProviderFromRange,
    mergeBotsByProviderMaps,
    buildBotsIpRegionRows,
    buildLabelSelector,
    computeSuccessRateSeries,
    mergeMetricSeries,
    resolveProviderLabel,
    isEnabled,
    tenantId,
    scenarioKey,
    rangeStartSec,
    rangeEndSec,
  ]);

  useEffect(() => {
    if (!isEnabled || !tenantId || !scenarioKey) return;

    fetchMetrics();
    if (rangeKey) return;

    const interval = setInterval(fetchMetrics, adaptiveIntervalMs);
    return () => clearInterval(interval);
  }, [adaptiveIntervalMs, fetchMetrics, isEnabled, rangeKey, tenantId, scenarioKey]);

  useEffect(() => {
    if (!isEnabled || !tenantId || !scenarioKey) return;

    const isSameBase = prevBaseKeyRef.current === baseKey;
    const prevRange = prevBufferSecondsRef.current;

    if (isSameBase && !rangeKey) {
      if (typeof prevRange === 'number' && bufferSeconds < prevRange) {
        const minTimestampMs = Date.now() - bufferSeconds * 1000;
        setMetrics((prev) => ({
          bandwidthDown: trimMetricSeries(prev.bandwidthDown, minTimestampMs),
          bandwidthUp: trimMetricSeries(prev.bandwidthUp, minTimestampMs),
          packetsUp: trimMetricSeries(prev.packetsUp, minTimestampMs),
          packetsDown: trimMetricSeries(prev.packetsDown, minTimestampMs),
          tcpConnections: trimMetricSeries(prev.tcpConnections, minTimestampMs),
          tcpConnectionsClosed: trimMetricSeries(prev.tcpConnectionsClosed, minTimestampMs),
          rpsTotal: trimMetricSeries(prev.rpsTotal, minTimestampMs),
          rpsOk: trimMetricSeries(prev.rpsOk, minTimestampMs),
          rpsError: trimMetricSeries(prev.rpsError, minTimestampMs),
          attackerErrors: trimMetricSeries(prev.attackerErrors, minTimestampMs),
          successRate: trimMetricSeries(prev.successRate, minTimestampMs),
          responseTime: trimMetricSeries(prev.responseTime, minTimestampMs),
          botsByProvider: Object.entries(prev.botsByProvider).reduce((acc, [provider, series]) => {
            const trimmed = trimMetricSeries(series, minTimestampMs);
            if (trimmed.length > 0) acc[provider] = trimmed;
            return acc;
          }, {} as Record<string, MetricDataPoint[]>),
          botsIpRegion: prev.botsIpRegion,
        }));
      }
      hasSeededRef.current = true;
    } else {
      const nextCacheEntry = metricsCache.get(cacheKey);
      const isFresh =
        !!nextCacheEntry &&
        Date.now() - nextCacheEntry.updatedAt < METRICS_CACHE_TTL_MS &&
        !rangeKey;
      setMetrics(isFresh ? nextCacheEntry!.data : createEmptyMetrics());
      hasSeededRef.current = isFresh ? nextCacheEntry!.seeded : false;
    }

    prevBaseKeyRef.current = baseKey;
    prevBufferSecondsRef.current = bufferSeconds;
  }, [
    baseKey,
    cacheKey,
    createEmptyMetrics,
    isEnabled,
    rangeKey,
    scenarioKey,
    tenantId,
    bufferSeconds,
    trimMetricSeries,
  ]);

  useEffect(() => {
    if (!isEnabled || !tenantId || !scenarioKey) return;
    metricsCache.set(cacheKey, {
      data: metrics,
      updatedAt: Date.now(),
      seeded: hasSeededRef.current,
    });
  }, [cacheKey, isEnabled, metrics, scenarioKey, tenantId]);

  return {
    metrics,
    loading,
    error,
    refetch: fetchMetrics,
  };
};


